import { marked } from "marked";
import DOMPurify from "dompurify";
import katex from "katex";
import mermaid from "mermaid";
import ABCJS from "abcjs";
import SmilesDrawer from "smiles-drawer";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import Prism from "prismjs";
import "prismjs/plugins/autoloader/prism-autoloader";
import type { MathPlaceholder } from "./types";
import { slugify, escapeHtml } from "./utils";

// Configure Prism autoloader to load languages from CDN
const plugins = Prism.plugins as Record<string, Record<string, unknown>>;
plugins["autoloader"]["languages_path"] =
  "https://cdn.jsdelivr.net/npm/prismjs@1.29.0/components/";

// Configure marked
marked.setOptions({ gfm: true, breaks: true });

// Mermaid state
let mermaidId = 0;
const mermaidDefinitions: string[] = [];

function currentThemeIsDark(): boolean {
  return document.documentElement.getAttribute("data-theme") === "dark";
}

// Initialize mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: currentThemeIsDark() ? "dark" : "default",
  securityLevel: "loose",
  fontFamily: "var(--font-sans)",
});

// --- Math pre-processing ---

const mathPlaceholders: MathPlaceholder[] = [];

function protectMath(md: string): string {
  mathPlaceholders.length = 0;
  let idx = 0;

  // Block math: $$...$$
  md = md.replace(/\$\$([\s\S]+?)\$\$/g, (_, tex: string) => {
    const id = `%%MATH_BLOCK_${idx++}%%`;
    mathPlaceholders.push({ id, tex: tex.trim(), displayMode: true });
    return id;
  });

  // Inline math: $...$
  md = md.replace(/(?<!\$)\$(?!\$)(.+?)(?<!\$)\$(?!\$)/g, (_, tex: string) => {
    const id = `%%MATH_INLINE_${idx++}%%`;
    mathPlaceholders.push({ id, tex: tex.trim(), displayMode: false });
    return id;
  });

  return md;
}

function restoreMath(html: string): string {
  for (const { id, tex, displayMode } of mathPlaceholders) {
    try {
      const rendered = katex.renderToString(tex, {
        displayMode,
        throwOnError: false,
        trust: true,
      });
      html = html.replace(id, rendered);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      html = html.replace(
        id,
        `<span class="math-error" title="${escapeHtml(message)}">${escapeHtml(tex)}</span>`,
      );
    }
  }
  return html;
}

// --- Mermaid pre-processing ---
// Store raw mermaid definitions before the HTML pipeline (marked → DOMPurify)
// can corrupt them. Uses the stored originals at render time instead of DOM textContent.

function protectMermaid(md: string): void {
  mermaidDefinitions.length = 0;
  const re = /```mermaid\n([\s\S]*?)```/g;
  let match;
  while ((match = re.exec(md)) !== null) {
    mermaidDefinitions.push(match[1].trim());
  }
}

// --- ABC notation pre-processing ---

const abcDefinitions: string[] = [];

function protectAbc(md: string): void {
  abcDefinitions.length = 0;
  const re = /```abc\n([\s\S]*?)```/g;
  let match;
  while ((match = re.exec(md)) !== null) {
    abcDefinitions.push(match[1].trim());
  }
}

// --- STL pre-processing ---

const stlDefinitions: string[] = [];
const stlCleanups: (() => void)[] = [];

function protectStl(md: string): void {
  stlDefinitions.length = 0;
  const re = /```stl\n([\s\S]*?)```/g;
  let match;
  while ((match = re.exec(md)) !== null) {
    stlDefinitions.push(match[1].trim());
  }
}

// --- SMILES pre-processing ---

const smilesDefinitions: string[] = [];

function protectSmiles(md: string): void {
  smilesDefinitions.length = 0;
  const re = /```smiles\n([\s\S]*?)```/g;
  let match;
  while ((match = re.exec(md)) !== null) {
    smilesDefinitions.push(match[1].trim());
  }
}

// --- GitHub-style alerts ---

function transformAlerts(html: string): string {
  return html.replace(
    /<blockquote>\s*<p>\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*<br\s*\/?>\s*([\s\S]*?)<\/p>\s*<\/blockquote>/gi,
    (_, type: string, content: string) => {
      const t = type.toLowerCase();
      const icons: Record<string, string> = {
        note: "\u2139\ufe0f",
        tip: "\ud83d\udca1",
        important: "\u203c\ufe0f",
        warning: "\u26a0\ufe0f",
        caution: "\ud83d\udd34",
      };
      return `<div class="alert alert-${t}"><div class="alert-title">${icons[t] || ""} ${type.toUpperCase()}</div><div>${content}</div></div>`;
    },
  );
}

// --- DOMPurify configuration ---

const PURIFY_CONFIG = {
  ADD_TAGS: [
    "input",
    "math",
    "mrow",
    "mi",
    "mo",
    "mn",
    "msup",
    "msub",
    "mfrac",
    "mover",
    "munder",
    "msqrt",
    "mtable",
    "mtr",
    "mtd",
    "mtext",
    "mspace",
    "annotation",
    "semantics",
    "mpadded",
    "menclose",
    "mglyph",
    "mstyle",
    "merror",
    "span",
    "svg",
    "g",
    "path",
    "rect",
    "circle",
    "ellipse",
    "line",
    "polyline",
    "polygon",
    "text",
    "tspan",
    "defs",
    "clippath",
    "marker",
    "foreignobject",
    "use",
    "symbol",
    "lineargradient",
    "radialgradient",
    "stop",
    "pattern",
    "mask",
    "image",
    "title",
    "desc",
  ],
  ADD_ATTR: [
    "checked",
    "disabled",
    "type",
    "class",
    "style",
    "aria-hidden",
    "xmlns",
    "mathvariant",
    "encoding",
    "stretchy",
    "fence",
    "separator",
    "accent",
    "accentunder",
    "displaystyle",
    "scriptlevel",
    "width",
    "height",
    "voffset",
    "lspace",
    "rspace",
    "linethickness",
    "columnalign",
    "columnspacing",
    "rowspacing",
    "viewBox",
    "fill",
    "stroke",
    "stroke-width",
    "stroke-linecap",
    "stroke-linejoin",
    "stroke-dasharray",
    "stroke-opacity",
    "fill-opacity",
    "transform",
    "d",
    "x",
    "y",
    "x1",
    "y1",
    "x2",
    "y2",
    "cx",
    "cy",
    "r",
    "rx",
    "ry",
    "dx",
    "dy",
    "points",
    "text-anchor",
    "dominant-baseline",
    "font-size",
    "font-family",
    "font-weight",
    "clip-path",
    "marker-end",
    "marker-start",
    "marker-mid",
    "refX",
    "refY",
    "markerWidth",
    "markerHeight",
    "orient",
    "gradientTransform",
    "gradientUnits",
    "spreadMethod",
    "offset",
    "stop-color",
    "stop-opacity",
    "patternUnits",
    "patternTransform",
    "xlink:href",
    "href",
    "preserveAspectRatio",
    "data-id",
    "data-node",
    "data-label",
    "data-et",
    "data-look",
    "id",
    "requiredFeatures",
    "systemLanguage",
    "opacity",
    "visibility",
  ],
  ALLOW_UNKNOWN_PROTOCOLS: true,
};

// --- DOM post-processing ---

function enhanceHeadings(preview: HTMLElement): void {
  const headings = preview.querySelectorAll("h1, h2, h3, h4, h5, h6");
  const slugCounts: Record<string, number> = {};

  headings.forEach((h) => {
    const text = h.textContent?.trim() || "";
    let slug = slugify(text);

    if (slugCounts[slug] !== undefined) {
      slugCounts[slug]++;
      slug = `${slug}-${slugCounts[slug]}`;
    } else {
      slugCounts[slug] = 0;
    }

    h.id = slug;

    const anchor = document.createElement("a");
    anchor.className = "heading-anchor";
    anchor.href = `#${slug}`;
    anchor.textContent = "#";
    h.appendChild(anchor);
  });
}

function enhanceCodeBlocks(preview: HTMLElement): void {
  preview.querySelectorAll("pre > code").forEach((code) => {
    const pre = code.parentElement!;
    if (pre.querySelector(".copy-btn")) return;

    const langMatch = code.className.match(/language-(\S+)/);
    const lang = langMatch ? langMatch[1] : "";

    if (lang) {
      const badge = document.createElement("span");
      badge.className = "code-lang";
      badge.textContent = lang;
      pre.insertBefore(badge, code);
    }

    const btn = document.createElement("button");
    btn.className = "copy-btn";
    btn.title = "Copy code";
    btn.textContent = "\u29c9";
    btn.addEventListener("click", () => {
      navigator.clipboard.writeText(code.textContent || "").then(() => {
        btn.classList.add("copied");
        btn.textContent = "\u2713";
        setTimeout(() => {
          btn.classList.remove("copied");
          btn.textContent = "\u29c9";
        }, 1500);
      });
    });
    pre.insertBefore(btn, code);
  });
}

async function renderMermaidBlocks(preview: HTMLElement): Promise<void> {
  const mermaidBlocks = preview.querySelectorAll("code.language-mermaid");
  if (mermaidBlocks.length === 0) return;

  mermaid.initialize({
    startOnLoad: false,
    theme: currentThemeIsDark() ? "dark" : "default",
    securityLevel: "loose",
    fontFamily: "var(--font-sans)",
  });

  let blockIndex = 0;
  for (const code of mermaidBlocks) {
    const pre = code.parentElement!;
    // Use stored definition to bypass any HTML pipeline corruption
    const graphDef = mermaidDefinitions[blockIndex++] ?? code.textContent ?? "";
    const id = `mermaid-graph-${mermaidId++}`;

    try {
      const { svg } = await mermaid.render(id, graphDef);
      const wrapper = document.createElement("div");
      wrapper.className = "mermaid-diagram";
      wrapper.innerHTML = svg;
      pre.replaceWith(wrapper);
    } catch (err) {
      console.warn("Mermaid render error:", err);
      const errDiv = document.createElement("div");
      errDiv.className = "mermaid-error";
      errDiv.textContent = `Mermaid error: ${err instanceof Error ? err.message : String(err)}`;
      pre.replaceWith(errDiv);
    }
  }
}

function renderAbcBlocks(preview: HTMLElement): void {
  const abcBlocks = preview.querySelectorAll("code.language-abc");
  if (abcBlocks.length === 0) return;

  let blockIndex = 0;
  for (const code of abcBlocks) {
    const pre = code.parentElement!;
    const abcString = abcDefinitions[blockIndex++] ?? code.textContent ?? "";

    try {
      const wrapper = document.createElement("div");
      wrapper.className = "abc-score";
      pre.replaceWith(wrapper);
      ABCJS.renderAbc(wrapper, abcString, {
        responsive: "resize",
        staffwidth: 700,
      });
    } catch (err) {
      console.warn("ABC render error:", err);
      const errDiv = document.createElement("div");
      errDiv.className = "abc-error";
      errDiv.textContent = `ABC notation error: ${err instanceof Error ? err.message : String(err)}`;
      pre.replaceWith(errDiv);
    }
  }
}

function renderSmilesBlocks(preview: HTMLElement): void {
  const smilesBlocks = preview.querySelectorAll("code.language-smiles");
  if (smilesBlocks.length === 0) return;

  const isDark = currentThemeIsDark();
  const drawer = new SmilesDrawer.SmiDrawer({ width: 300, height: 200 });

  let blockIndex = 0;
  for (const code of smilesBlocks) {
    const pre = code.parentElement!;
    const smilesStr = smilesDefinitions[blockIndex++] ?? code.textContent ?? "";

    try {
      const wrapper = document.createElement("div");
      wrapper.className = "smiles-structure";

      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      wrapper.appendChild(svg);
      pre.replaceWith(wrapper);

      drawer.draw(
        smilesStr,
        svg,
        isDark ? "dark" : "light",
        () => {},
        (err: Error) => {
          console.warn("SMILES render error:", err);
          wrapper.innerHTML = "";
          wrapper.className = "smiles-error";
          wrapper.textContent = `SMILES error: ${err instanceof Error ? err.message : String(err)}`;
        },
      );
    } catch (err) {
      console.warn("SMILES render error:", err);
      const errDiv = document.createElement("div");
      errDiv.className = "smiles-error";
      errDiv.textContent = `SMILES error: ${err instanceof Error ? err.message : String(err)}`;
      pre.replaceWith(errDiv);
    }
  }
}

function renderStlBlocks(preview: HTMLElement): void {
  for (const fn of stlCleanups) fn();
  stlCleanups.length = 0;

  const stlBlocks = preview.querySelectorAll("code.language-stl");
  if (stlBlocks.length === 0) return;

  const loader = new STLLoader();
  let blockIndex = 0;

  for (const code of stlBlocks) {
    const pre = code.parentElement!;
    const stlString = stlDefinitions[blockIndex++] ?? code.textContent ?? "";

    try {
      const wrapper = document.createElement("div");
      wrapper.className = "stl-viewer";
      pre.replaceWith(wrapper);

      const width = wrapper.clientWidth || 700;
      const height = 400;

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
      });
      renderer.setSize(width, height);
      renderer.setPixelRatio(window.devicePixelRatio);
      wrapper.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);

      scene.add(new THREE.AmbientLight(0x888888));
      const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
      dirLight.position.set(1, 2, 3);
      scene.add(dirLight);
      const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
      dirLight2.position.set(-2, -1, -1);
      scene.add(dirLight2);

      const encoder = new TextEncoder();
      const buffer = encoder.encode(stlString).buffer;
      const geometry = loader.parse(buffer);
      geometry.computeVertexNormals();

      geometry.computeBoundingBox();
      const bbox = geometry.boundingBox!;
      const center = new THREE.Vector3();
      bbox.getCenter(center);
      geometry.translate(-center.x, -center.y, -center.z);

      const size = new THREE.Vector3();
      bbox.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);
      if (maxDim > 0) geometry.scale(2 / maxDim, 2 / maxDim, 2 / maxDim);

      const material = new THREE.MeshPhongMaterial({
        color: 0x58a6ff,
        specular: 0x222222,
        shininess: 40,
      });
      scene.add(new THREE.Mesh(geometry, material));

      camera.position.set(3, 2, 3);
      camera.lookAt(0, 0, 0);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.autoRotate = true;
      controls.autoRotateSpeed = 2;

      let animId: number;
      const animate = () => {
        animId = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      stlCleanups.push(() => {
        cancelAnimationFrame(animId);
        controls.dispose();
        geometry.dispose();
        material.dispose();
        renderer.dispose();
      });
    } catch (err) {
      console.warn("STL render error:", err);
      const errDiv = document.createElement("div");
      errDiv.className = "stl-error";
      errDiv.textContent = `STL error: ${err instanceof Error ? err.message : String(err)}`;
      pre.replaceWith(errDiv);
    }
  }
}

// --- Main render function ---

export function renderMarkdown(
  editorValue: string,
  preview: HTMLElement,
): void {
  let md = editorValue;

  try {
    protectMermaid(md);
    protectAbc(md);
    protectStl(md);
    protectSmiles(md);
    md = protectMath(md);
    let html = marked.parse(md) as string;
    html = restoreMath(html);
    html = transformAlerts(html);
    html = DOMPurify.sanitize(html, PURIFY_CONFIG);

    preview.innerHTML = html;

    enhanceHeadings(preview);
    enhanceCodeBlocks(preview);
    renderAbcBlocks(preview);
    renderSmilesBlocks(preview);
    renderStlBlocks(preview);
    renderMermaidBlocks(preview);

    // Syntax highlight (skip special blocks)
    preview
      .querySelectorAll(
        'pre code[class*="language-"]:not(.language-mermaid):not(.language-abc):not(.language-stl):not(.language-smiles)',
      )
      .forEach((block) => {
        Prism.highlightElement(block as HTMLElement);
      });

    // Handle hash navigation
    if (location.hash) {
      const target = document.getElementById(location.hash.slice(1));
      if (target) {
        setTimeout(
          () => target.scrollIntoView({ behavior: "smooth", block: "start" }),
          100,
        );
      }
    }
  } catch (err) {
    console.error("Render error:", err);
    const message = err instanceof Error ? err.message : String(err);
    preview.innerHTML = `<div style="color:var(--danger);padding:20px;font-family:var(--font-mono);font-size:var(--fs-sm);">Render error: ${escapeHtml(message)}</div>`;
  }
}
